import * as ExcelJS from "exceljs";

/**
 * Palette + helpers Excel communs aux 3 widgets Qualité (NC stats,
 * closure metrics, supplier compliance). Reprend la charte des autres
 * exports (cf. valeur-stock-excel.utils.ts) pour cohérence visuelle.
 *
 * Note couleurs : on aligne sur la palette JustUp (vert / rouge / orange
 * vifs) mais on conserve les fonds pastel pour les stats cards (lisibilité
 * sur fond clair Excel).
 */

export const QUALITE_C = {
  // Header / titres / pieds de tableau
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  // Sections & lignes
  groupBg: "FFE8EDF3",
  groupFont: "FF1B2A4A",
  rowAltBg: "FFF5F7FA",
  border: "FFD0D5DD",
  rowBorder: "FFE4E7EC",
  // Stats cards (pastel cohérent avec valeur-stock-excel)
  statGreen: "FFE6FFF5",
  statBlue: "FFE6F0FF",
  statPurple: "FFF0E6FF",
  statOrange: "FFFFF0E0",
  statRed: "FFFFEAEA",
  titleColor: "FF1B2A4A",
  // Sévérités (alignées sur la palette JustUp habituelle)
  severityCritique: "FFDF003D",
  severityMajeure: "FFFF6543",
  severityMineure: "FF11C85F",
  // Origine NC — palette Chart.js par défaut pour cohérence avec les
  // autres widgets graphiques de l'app (cf. `widget-valeur-par-famille`).
  origineInterne: "FF36A2EB",
  origineExterne: "FFFF6384",
  origineMixte: "FFFF9F40",
  origineNonRenseigne: "FFC9CBCF",
};

/**
 * Palette Chart.js par défaut (Chart.js v4) — utilisée par les autres
 * widgets de l'app comme `valeur-par-famille`. On la réutilise pour les
 * exports Excel pour avoir une cohérence visuelle bout-en-bout.
 */
export const QUALITE_CHART_COLORS = [
  "#36A2EB",
  "#FF6384",
  "#FF9F40",
  "#FFCD56",
  "#4BC0C0",
  "#9966FF",
  "#C9CBCF",
  "#8FCBFF",
  "#FFA1B5",
  "#A0D8B3",
];

export function qualiteHeaderStyle(cell: ExcelJS.Cell): void {
  cell.font = { bold: true, size: 10, color: { argb: QUALITE_C.headerFont } };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: QUALITE_C.headerBg },
  };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.border = {
    bottom: { style: "medium", color: { argb: QUALITE_C.headerBg } },
  };
}

export function qualiteSubHeaderStyle(cell: ExcelJS.Cell): void {
  cell.font = { bold: true, size: 10, color: { argb: QUALITE_C.groupFont } };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: QUALITE_C.groupBg },
  };
  cell.alignment = { horizontal: "left", vertical: "middle" };
  cell.border = {
    top: { style: "thin", color: { argb: QUALITE_C.border } },
    bottom: { style: "thin", color: { argb: QUALITE_C.border } },
  };
}

export function qualiteStatStyle(cell: ExcelJS.Cell, bg: string): void {
  cell.font = { bold: true, size: 11, color: { argb: QUALITE_C.titleColor } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.border = {
    top: { style: "thin", color: { argb: QUALITE_C.border } },
    bottom: { style: "thin", color: { argb: QUALITE_C.border } },
    left: { style: "thin", color: { argb: QUALITE_C.border } },
    right: { style: "thin", color: { argb: QUALITE_C.border } },
  };
}

export function qualiteStatLabelStyle(cell: ExcelJS.Cell, bg: string): void {
  cell.font = { size: 9, color: { argb: QUALITE_C.titleColor }, italic: true };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
}

export function qualiteRowStyle(cell: ExcelJS.Cell, alt = false): void {
  cell.font = { size: 9, color: { argb: "FF101828" } };
  cell.border = {
    bottom: { style: "hair", color: { argb: QUALITE_C.rowBorder } },
  };
  if (alt) {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: QUALITE_C.rowAltBg },
    };
  }
}

export function qualiteSectionTitle(cell: ExcelJS.Cell, label: string): void {
  cell.value = label;
  cell.font = { bold: true, size: 13, color: { argb: QUALITE_C.titleColor } };
  cell.alignment = { horizontal: "left", vertical: "middle" };
}

export function colLetter(col: number): string {
  let s = "";
  let c = col;
  while (c > 0) {
    c -= 1;
    s = String.fromCharCode(65 + (c % 26)) + s;
    c = Math.floor(c / 26);
  }
  return s;
}

export function downloadXlsx(buffer: ArrayBuffer, filename: string): void {
  const blob = new Blob([new Uint8Array(buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 10000);
}

/**
 * Render a Chart.js chart to PNG base64 offscreen — réutilisable pour
 * intégrer un graphique dans une worksheet. La fonction `build` reçoit le
 * contexte canvas et doit retourner un objet exposant `destroy()` (chart
 * Chart.js standard).
 */
export function renderChartToPng(
  build: (ctx: CanvasRenderingContext2D) => { destroy: () => void },
  width = 500,
  height = 320,
): string | null {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const chart = build(ctx);
    const dataUrl = canvas.toDataURL("image/png");
    chart.destroy();
    return dataUrl.replace(/^data:image\/png;base64,/, "");
  } catch {
    return null;
  }
}

/** Today as YYYY-MM-DD pour les noms de fichiers d'export. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
